import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { ArrowLeft, ChevronDown, ChevronUp, Users, User, Wallet } from 'lucide-react';
import api from '../../utils/api';
import { beijingToday } from '../../utils/datetime';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';

import { fmtMoney as fmt } from '../../utils/clipboard';

// 业务标签（排五 / 证券）：统计页里排五、证券的结算记录合在一起，用它区分
function BizTag({ biz }) {
  const puzzle = biz === 'puzzle';
  return (
    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '1px 6px', marginRight: 6, background: puzzle ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)', color: puzzle ? '#8944ab' : 'var(--c-primary)' }}>{puzzle ? '证券' : '排五'}</span>
  );
}

function AgentBlock({ data }) {
  const [expanded, setExpanded] = useState(false);
  const { agent, personal, sub, totalPayable } = data;   // data.payoutBy：按业务拆分的应付

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}>
      {/* 代理商头部 */}
      <div style={{ padding: '14px 16px', background: 'var(--c-primary-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={16} color="var(--c-primary)" />
            <span style={{ fontWeight: 700, fontSize: 15 }}>{agent.name || agent.phone}</span>
            <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{agent.phone}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}>管理员应付</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--c-primary)' }}>¥{fmt(totalPayable)}</div>
            {data.payoutBy && (data.payoutBy.legacy.amount > 0 || data.payoutBy.puzzle.amount > 0) && (
              <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 3 }}>
                排五 ¥{fmt(data.payoutBy.legacy.amount)} · 证券 ¥{fmt(data.payoutBy.puzzle.amount)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 汇总行 */}
      <div style={{ padding: '12px 16px', display: 'flex', gap: 0, borderBottom: '0.5px solid var(--c-border)' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginBottom: 3 }}>个人报酬</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-success)' }}>¥{fmt(personal.amount)}</div>
        </div>
        <div style={{ width: 0.5, background: 'var(--c-border)', margin: '4px 0' }} />
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginBottom: 3 }}>下级成本</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-text)' }}>¥{fmt(sub.totalCost)}</div>
        </div>
        <div style={{ width: 0.5, background: 'var(--c-border)', margin: '4px 0' }} />
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginBottom: 3 }}>佣金差价</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-accent)' }}>¥{fmt(sub.commission)}</div>
        </div>
      </div>

      {/* 个人接量明细 */}
      {personal.amount > 0 && (
        <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--c-border)', background: 'rgba(52,199,89,0.04)' }}>
          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>个人接量</div>
          <div style={{ fontSize: 13, color: 'var(--c-text)', display: 'flex', gap: 16 }}>
            <span>接量 <strong>{personal.count}</strong> 条</span>
            <span style={{ color: 'var(--c-success)' }}>报酬 <strong>¥{fmt(personal.amount)}</strong></span>
          </div>
        </div>
      )}

      {/* 下级用户展开 */}
      {sub.count > 0 && (
        <>
          <div
            onClick={() => setExpanded(!expanded)}
            style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Users size={14} /> 下级用户 {sub.count} 人
              <span style={{ color: 'var(--c-text-tertiary)' }}>· 用户实收 ¥{fmt(sub.totalUserAmount)}</span>
            </span>
            {expanded ? <ChevronUp size={16} color="var(--c-text-tertiary)" /> : <ChevronDown size={16} color="var(--c-text-tertiary)" />}
          </div>

          {expanded && (
            <div style={{ borderTop: '0.5px solid var(--c-border)' }}>
              {sub.records.map(r => (
                <div key={r.id} style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center' }}><BizTag biz={r.business} />{r.user_name || r.user_phone}</div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                      {r.count || 0} {r.business === 'puzzle' ? '单' : '条'}{r.price != null && r.price > 0 ? ` × ¥${r.price}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
                      用户实收 ¥{fmt(r.total_amount)} · 基准成本 ¥{fmt(r.base_cost)} · 佣金 ¥{fmt(r.profit)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-primary)' }}>¥{fmt(r.base_cost)}</div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>管理员付</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SettlementOverviewPage() {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(beijingToday());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settlement/admin-overview', { params: { date } });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'settlement_updated') fetchData();
    });
  }, [subscribe, fetchData]);

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Wallet size={16} /> 结算统计
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* 日期选择 */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', flexShrink: 0 }}>查看日期</span>
          <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} style={{ flex: 1 }} />
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : !data ? null : (
          <>
            {/* 总计横幅 */}
            <div className="card gradient-blue" style={{ color: 'white', padding: 16, marginBottom: 4 }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>今日管理员应付总额</div>
              <div style={{ fontSize: 28, fontWeight: 800 }}>¥{fmt(data.grandTotal)}</div>
              {data.grandByBusiness && (
                <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, opacity: 0.95 }}>
                  <span>排五 ¥{fmt(data.grandByBusiness.legacy.amount)}<span style={{ opacity: 0.7 }}> · {data.grandByBusiness.legacy.count} 单</span></span>
                  <span>证券 ¥{fmt(data.grandByBusiness.puzzle.amount)}<span style={{ opacity: 0.7 }}> · {data.grandByBusiness.puzzle.count} 单</span></span>
                </div>
              )}
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                代理商 {data.agents.filter(a => a.totalPayable > 0).length} 人 · 直属用户 {data.direct.records.length} 人
              </div>
            </div>

            {/* 代理商列表 */}
            {data.agents.filter(a => a.totalPayable > 0 || a.sub.count > 0 || a.personal.amount > 0).length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text-secondary)', padding: '10px 4px 6px' }}>
                  代理商结算
                </div>
                {data.agents
                  .filter(a => a.totalPayable > 0 || a.sub.count > 0 || a.personal.amount > 0)
                  .map(a => <AgentBlock key={a.agent.id} data={a} />)
                }
              </>
            )}

            {/* 直属用户 */}
            {data.direct.records.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text-secondary)', padding: '10px 4px 6px' }}>
                  直属用户结算（管理员直接付款）
                </div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {data.direct.records.map((r, i) => (
                    <div key={r.id} style={{
                      padding: '12px 16px',
                      borderBottom: i < data.direct.records.length - 1 ? '0.5px solid var(--c-border)' : 'none',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center' }}><BizTag biz={r.business} />{r.user_name || r.user_phone}</div>
                        <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                          {r.count || 0} {r.business === 'puzzle' ? '单' : '条'}{r.price != null && r.price > 0 ? ` × ¥${r.price}` : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-success)' }}>¥{fmt(r.total_amount)}</div>
                    </div>
                  ))}
                  <div style={{ padding: '10px 16px', background: 'var(--c-primary-light)', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-primary)' }}>直属用户合计</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--c-primary)' }}>¥{fmt(data.direct.total)}</span>
                  </div>
                </div>
              </>
            )}

            {/* 空状态 */}
            {data.grandTotal === 0 && (
              <div className="card empty-state" style={{ padding: 40 }}>
                <Wallet size={40} color="var(--c-text-secondary)" strokeWidth={1.2} />
                <p style={{ marginTop: 12 }}>今日暂无结算记录</p>
              </div>
            )}
          </>
        )}
      </div>
      <TabBar mode="admin" />
    </div>
  );
}
